import React, { useState, useEffect, useRef, Fragment } from 'react';
import { UserAccess, UserHasAccess } from "components/user-access";
import { Link } from "react-router-dom";
import { PageRestricted } from "pages/errors/page-restricted";
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'hooks/useDebounce/useDebounce';
import { SetFiltering } from 'state/actions';
import { EmployeeService, UserService } from 'services';
import { DateToLocal } from 'components/dates';
import { NotificationToaster } from 'components/notifications';
import { Intent } from '@blueprintjs/core';
import { ListingTable } from 'components/listing';
import { Filter, FilterButton } from 'components/filtering';
import { Button, LinkButton } from 'components/buttons';
import { Tooltip } from 'components/tooltip';
import { FormLocationSelector, FormTextInput } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import PropTypes from 'prop-types';
import { SignoffDocumentModal } from 'pages/policy/document/signoff/SignoffDocumentModal';
import { ViewDocumentModal } from 'pages/policy/shared/';
import { PolicyDocumentTypes } from 'pages/policy/document/constants/PolicyDocumentTypes';

export function PolicySignoffListing({
    employeeId = null,
    title
}) {

    const isForSingleEmployee = !!employeeId;
    const requiredAction = isForSingleEmployee ? "Documents:View" : "Documents:Manage";
    const performAction = "Documents:PeformPolicySignOff";
    const defaultRecordSize = 25;
    const debounceTimeout = 750;
    const filterName = 'policySignoffActions';
    const [initalSetupComplete, setInitalSetupComplete] = useState(false);
    const [filterSetupComplete, setFilterSetupComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [noDataMessage, setNoDataMessage] = useState(`There is no data to display for ${title}`);
    const dispatch = useDispatch();
    const BUSINESS_AREA_IDS = [1];
    const DEFAULT_SORT_COL = 'DUEFROMDATE';
    const canSignoffDocument = UserHasAccess([performAction]);
    const [signoffModalVisible, setSignoffModalVisible] = useState(false);
    const [policySignoffActionItem, setPolicySignoffActionItem] = useState(null);
    const [openViewSignedDocumentModal, setOpenViewSignedDocumentModal] = useState(false);

    const tableHeaders = isForSingleEmployee
        ? ["Policy", "Document", "Version", "Sign off due date", "Signed Off Date", "Signed Off", "Actions"]
        : ["Employee", "Employee Category", "Location", "Policy", "Document", "Version", "Sign off due date", "Signed Off Date", "Signed Off"];

    const sortableHeaders = isForSingleEmployee
        ? [
            { headerName: "Policy", sortName: "POLICYNAME" },
            { headerName: "Document", sortName: "DOCUMENTNAME" },
            { headerName: "Version", sortName: "VERSION" },
            { headerName: "Sign off due date", sortName: "DUEFROMDATE" },
            { headerName: "Signed Off Date", sortName: "COMPLETEDDATE" },
            { headerName: "Signed Off", sortName: "COMPLETEDDATE" },
        ]
        : [
            { headerName: "Employee", sortName: "EMPLOYEEFULLNAME" },
            { headerName: "Employee Category", sortName: "EMPLOYEECATEGORY" },
            { headerName: "Location", sortName: "LOCATIONNAME" },
            { headerName: "Policy", sortName: "POLICYNAME" },
            { headerName: "Document", sortName: "DOCUMENTNAME" },
            { headerName: "Version", sortName: "VERSION" },
            { headerName: "Sign off due date", sortName: "DUEFROMDATE" },
            { headerName: "Signed Off Date", sortName: "COMPLETEDDATE" },
            { headerName: "Signed Off", sortName: "COMPLETEDDATE" },
        ];

    const [sortedBy, setSortedBy] = useState(sortableHeaders.find(header => header.sortName === DEFAULT_SORT_COL));
    const [sortedDir, setSortedDir] = useState("A");

    const [totalRecords, setTotalRecords] = useState(0);
    const [tableRows, setTableRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);

    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);

    const currentPageNumber = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentSearchTerm = useRef();
    const currentAppliedFilters = useRef();
    const currentAppliedLocations = useRef();

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const selectedFilters = useSelector(state => state.filters[filterName]);

    const [locationList, setLocationList] = useState({ location: [], hierarchy: [] });
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [locationsLoading, setLocationsLoading] = useState(true);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);


    if (!selectedFilters) {
        dispatch(SetFiltering(filterName, []))
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        });
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
    }

    function buildRowFromData(d, index) {
        return isForSingleEmployee
            ? [
                d.policyName,
                d.documentName,
                d.documentVersionNumber,
                <DateToLocal
                    key={`${d.policyId}-${index}`}
                    format="DD/MM/YYYY">
                    {d.dueFromDate}
                </DateToLocal>,
                <DateToLocal
                    key={`${d.policyId}-${index}`}
                    format="DD/MM/YYYY">
                    {d.completedDate}
                </DateToLocal>,
                d.completedDate ? 'Yes' : 'No',
                <ShowHide key={`complete-showhide-${d.documentId}-${d.dueFromDate}`} evaluator={!d.completedDate}
                    show={
                        <Tooltip key={`complete-tooltip-${d.documentId}-${d.dueFromDate}`} content={!canSignoffDocument ? undefined : "Signoff document"} position="top">
                            <LinkButton key={`complete-button-${d.documentId}-${d.dueFromDate}`} minimal={true} large={false} onClick={() => openSignoffModal(d)} intent="save" disabled={!canSignoffDocument} icon="edit" />
                        </Tooltip>
                    } hide={
                        <Tooltip key={`view-signed-tootip-${d.documentId}-${d.dueFromDate}`} content="View signed document" position="top">
                            <Button key={`view-signed-button-${d.documentId}-${d.dueFromDate}`} minimal={true} large={false} onClick={() => (viewSignedDocument(d))} intent="save" icon="eye-open" />
                        </Tooltip>
                    }
                />
            ]
            : [
                <div key={`${d.employeeId}-${index}`}>{<Link to={`/employee/${d.employeeId}/documents/policies`}>{`${d.employeeFullName}`}</Link>}</div>,
                d.employeeCategory,
                d.locationName,
                d.policyName,
                d.documentName,
                d.documentVersionNumber,
                <DateToLocal
                    key={`${d.employeeId}-${index}`}
                    format="DD/MM/YYYY">
                    {d.dueFromDate}
                </DateToLocal>,
                <DateToLocal
                    key={`${d.employeeId}-${index}`}
                    format="DD/MM/YYYY">
                    {d.completedDate}
                </DateToLocal>,
                d.completedDate ? 'Yes' : 'No'
            ];
    }

    function fetchTableData(forceRefresh) {
        if (initalSetupComplete && !locationSelectorOpen) {
            if (
                currentPageNumber.current === pageNumber &&
                currentTableSortBy.current === sortedBy &&
                currentTableSortDir.current === sortedDir &&
                currentSearchTerm.current === debouncedSearchTerm &&
                currentAppliedFilters.current === appliedFilters &&
                currentAppliedLocations.current === selectedLocations &&
                !forceRefresh
            ) {
                //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentPageNumber.current = pageNumber;
            currentTableSortBy.current = sortedBy;
            currentTableSortDir.current = sortedDir;
            currentSearchTerm.current = debouncedSearchTerm;
            currentAppliedFilters.current = appliedFilters;
            currentAppliedLocations.current = selectedLocations;

            setIsLoading(true);

            const searchRequest = {
                requestCount: defaultRecordSize,
                pageNumber: pageNumber,
                sortField: sortedBy.sortName,
                sortDirection: sortedDir,
                filters: [
                    ...(currentAppliedFilters.current ?? []).map(f => ({
                        key: f.name,
                        ...f,
                    })),
                    ...(currentAppliedLocations.current ?? []).map(location => ({
                        key: 'location',
                        value: location,
                    })),
                ],
                searchTerm: debouncedSearchTerm,
                ...(isForSingleEmployee && { employeeId })
            };

            const fetchFunction = isForSingleEmployee
                ? EmployeeService.getPolicySignOffActionsForEmployee
                : EmployeeService.getPolicySignOffActionsForAccount;

            fetchFunction(searchRequest).then(
                (p) => {
                    let data = [];

                    if (p?.paging.data != null) {
                        data = p.paging.data.map((d, index) => {
                            return buildRowFromData(d, index);
                        });
                    }

                    setTableRows(data);
                    setTotalRecords(p.paging.totalCount);
                    setIsLoading(false);
                },
                () => {
                    const notification = "Could not retrieve policy signoff actions, please try again";

                    NotificationToaster.show(
                        Intent.DANGER,
                        notification
                    );
                    setNoDataMessage(notification);
                    setIsLoading(false);
                }
            );
        }
    }

    useEffect(() => {
        if (selectedLocations.length > 0 || isForSingleEmployee) {
            setLocationsLoading(false);
        }
    }, [selectedLocations, isForSingleEmployee]);

    useEffect(() => {
        setInitalSetupComplete(!locationsLoading && filterSetupComplete);
    }, [locationsLoading, filterSetupComplete]);

    useEffect(() => {
        if (!isForSingleEmployee) {
            const fetchLocations = async () => {
                try {
                    const locationsResponse = await UserService.getLoggedInUserLocationsForMultipleBusinessAreas(BUSINESS_AREA_IDS, true, true);
                    setLocationList(locationsResponse);
                    setSelectedLocations(locationsResponse.location[0].items.map((i) => i.id));
                } catch {
                    NotificationToaster.show(Intent.DANGER, 'Failed to load location data, please try again.');
                }
            };

            fetchLocations();
        }
    }, [isForSingleEmployee]);

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        initalSetupComplete,
        appliedFilters,
        locationSelectorOpen
    ]);

    useEffect(() => {
        setPageFilters([
            {
                name: 'Status',
                displayName: 'Status',
                items: [{ id: 'signedoff', name: 'Signed Off' }, { id: 'outstanding', name: 'Outstanding' }]
            }
        ])


        setFilterSetupComplete(true);
    }, []);

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
    }

    function onLocationListOpened() {
        setLocationSelectorOpen(true);
    }

    function onLocationListClose() {
        setLocationSelectorOpen(false);
    }

    function onSignoffComplete() {
        setSignoffModalVisible(false);
        fetchTableData(true);
    }

    function openSignoffModal(data) {
        setPolicySignoffActionItem(data);
        setSignoffModalVisible(true);
    }

    function viewSignedDocument(data) {
        setPolicySignoffActionItem(data);
        setOpenViewSignedDocumentModal(true);
    }

    return (
        <Fragment>
            <UserAccess perform={requiredAction}
                yes={() => (
                    <div>
                        <div className="filter-controls">
                            <div className="inline-items spacer-bottom">
                                <div className="inline-item fill">
                                    <div className="form-field-full">
                                        <FormTextInput
                                            placeholder="Search"
                                            onChange={onSearchChange}
                                            value={searchTerm}
                                            large
                                            disabled={isLoading}
                                            icon="search"
                                            id="policy-signoff-action-search-field"
                                        />
                                    </div>
                                </div>
                                <ShowHide evaluator={!isForSingleEmployee} show={(
                                    <div className="inline-item">
                                        <FormLocationSelector
                                            businessArea={"Tacho"}
                                            loading={isLoading}
                                            selectedLocations={selectedLocations}
                                            setSelectedLocations={onSelectedLocationsChange}
                                            useHierarchy={useHierarchy}
                                            setUseHierarchy={setUseHierarchy}
                                            useLocationDefaults={false}
                                            useCustomLocations
                                            customLocations={locationList}
                                            locationDefaultsToTrue
                                            onLocationListOpened={onLocationListOpened}
                                            onLocationListClose={onLocationListClose}
                                        />
                                    </div>
                                )} />
                                <div className="inline-item">
                                    <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                                </div>
                            </div>
                            <Filter
                                filterName={filterName}
                                visible={showFilters}
                                filters={pageFilters}
                                onUpdate={onFilterChange}
                                onClickOutside={onToggleFilter}
                            />
                        </div>

                        <ListingTable
                            showPerPage={false}
                            pageSize={defaultRecordSize}
                            headers={tableHeaders}
                            data={tableRows}
                            totalRecordCount={totalRecords}
                            loadingData={isLoading}
                            onPagingChange={onPagingChange}
                            noDataMessage={noDataMessage}
                            noDataHeading={title}
                            sortable
                            sortableHeaders={sortableHeaders}
                            sortedBy={sortedBy}
                            sortedDir={sortedDir}
                            onSort={onTableSort}
                        />
                        <SignoffDocumentModal
                            onClose={() => setSignoffModalVisible(false)}
                            isOpen={signoffModalVisible}
                            onSignoffComplete={onSignoffComplete}
                            policySignoffActionItem={policySignoffActionItem}
                        />
                        <ViewDocumentModal
                            isOpen={openViewSignedDocumentModal}
                            onClose={() => setOpenViewSignedDocumentModal(false)}
                            documentData={{
                                documentName: policySignoffActionItem?.name,
                                documentStorageIdentifier: policySignoffActionItem?.signedDocumentStorageIdentifier,
                                documentType: PolicyDocumentTypes.SIGNED,
                            }}
                        />
                    </div>
                )}
                no={() => (
                    <PageRestricted />
                )}
            />
        </Fragment>
    );
}

PolicySignoffListing.propTypes = {
    employeeId: PropTypes.number,
};
